import moment from 'moment-timezone';
import { isEmpty } from 'lodash';

import Credit from "../../../../Entities/Credit";
import CreditEntry from "../../../../Entities/CreditEntry";
import Subscription from "../../../../Entities/Subscription";
import PaymentApplicationMonth from '../../../../Entities/PaymentApplicationMonth';

import {
    queryGetCredit,
    queryGetFacilityPaymentTypes,
    queryGetFacilitySettings,
    queryGetPaymentApplicationMonths,
    queryListSubscriptions,
} from "../../../../Queries";

import { getFacility } from '../../../../Utils/facility';

import featureFlags from "../../../../featureFlags";

const subscriptionsQueryFields = [
    {
        'subscriptions': [
            'id',
            'subscription_name',
            'account_id',
            {
                'payor': [
                    'readable_id',
                    'billing_firstname',
                    'billing_lastname'
                ]
            },
            {
                'billing_profile': [
                    'name'
                ]
            }
        ]
    }
];

const creditQueryFields = [
    'id',
    'readable_id',
    'amount',
    'balance',
    'created_at',
    'subscription_id',
    'notes',
    'application_type',
    'payment_type',
    {
        'payment_meta': [
            'check_number'
        ]
    },
    {
        'credit_entries': [
            'id',
            'type',
            'amount',
            'amount_tax_1',
            'amount_tax_2',
            'amount_tax_3',
            'amount_tax_4',
            'application_date',
            {
                'payment': [
                    'id',
                    {
                        'invoice': [
                            'readable_id'
                        ],
                    },
                    {
                        'line_item_payments': [
                            {
                                'payment_entry': [
                                    {
                                        'transaction': [
                                            'application_date'
                                        ]
                                    }
                                ]
                            }
                        ]
                    }
                ]
            },
            'created_at',
        ]
    },
    {
        'payment': [
            {
                'invoice': [
                    'readable_id'
                ]
            }
        ]
    },
    {
        'subscription': [
            'id',
            featureFlags.ACCOUNT_ID ? 'account_id' : '',
            featureFlags.SUBSCRIPTION_NAME ? 'subscription_name' : '',
            {
                'payor': [
                    'readable_id',
                    'billing_firstname',
                    'billing_lastname',
                    featureFlags.SUBSCRIPTION_NAME ? 'info_name' : ''
                ]
            }
        ]
    }
]

const facilitySettingsQueryFields = [
    'accounting_method'
];

class CreditPanelConfig {
    initialState = {
        credit: null,
        applicationDate: null,
    };

    fieldConfiguration = {
        credit: {},
        facilityTimezone: null,
        now: new Date()
    };

    constructor({ newMode, creditId, facilityId }) {
        this.newMode = newMode;
        this.creditId = creditId;
        this.facilityId = facilityId;
    }

    loadConfig = async () => {
        const initializationTasks = [
            this.fetchCreditFieldValues(),
            this.getCreditApplicationTypes(),
            this.getApplicationDateConfig(),
            this.getFacilityTimezone(),
        ];

        if (this.newMode) {
            initializationTasks.push(this.createNewCredit());
            initializationTasks.push(this.fetchSubscriptions());
        } else {
            initializationTasks.push(this.fetchCredit());
        }

        await Promise.all(initializationTasks);

        if (featureFlags.CREDIT_UPDATES && !this.newMode) {
            this.setCreditEntryTypeLabels();
        }

        return this;
    }

    getApplicationDateConfig = async () => {
        const applicationMonthsQuery = [
            'month',
            'year',
            'status',
        ];

        const monthsResponse = await queryGetPaymentApplicationMonths({
            facility_id: this.facilityId
        }, applicationMonthsQuery);

        const responseMonths = monthsResponse.data.getPaymentApplicationMonths;
        const paymentApplicationMonths = responseMonths.map(month => PaymentApplicationMonth.createFromQueryValues(month));

        const facilitySettingsFields = ['future_month_application'];

        const settingsResponse = await queryGetFacilitySettings({
            facility_id: this.facilityId
        }, facilitySettingsFields);

        const paymentTypesResponse = await queryGetFacilityPaymentTypes(
            { facilityId: this.facilityId },
            [{ 'paymentTypes': ['paymentTypeName'] }],
        )
        const paymentTypes = paymentTypesResponse.data.getFacilityPaymentTypes?.paymentTypes?.map(({ paymentTypeName }) => paymentTypeName);

        const facilitySettings = settingsResponse.data.getFacilitySettings;
        const futureMonthApplication = facilitySettings?.future_month_application;
        this.fieldConfiguration.payment_type = { options: this.fetchPaymentFieldValues(paymentTypes) };

        let datesToExclude = [];
        if (paymentApplicationMonths.length) {
            const closedMonths = paymentApplicationMonths.filter(month => month.fields.status.value === "CLOSED");

            // Exclude every day of every closed month
            datesToExclude = closedMonths.map(closedMonth => {
                const year = closedMonth.fields.year.value;

                let month = closedMonth.fields.month.value.toLowerCase();
                month = month.charAt(0).toUpperCase() + month.slice(1);
                month = moment(`${month}, ${year}`, 'MMM YYYY');

                const daysInMonth = month.daysInMonth();
                return [...new Array(daysInMonth)].map((_, i) => {
                    return new Date(month.date(i + 1));
                });
            }).flat();
        }

        const startDate = new Date(moment().subtract(1, "year").startOf("month").add(1, "day").format("YYYY-MM-DD"));
        const tomorrow = new Date(moment().add(1, "day").format('YYYY-MM-DD'));
        const currentMonth = new Date(moment().startOf("month").add(1, "month").format('YYYY-MM-DD'));

        const maxDate = isEmpty(datesToExclude) ? tomorrow :
            futureMonthApplication === "NOT_ALLOWED" ? tomorrow :
                futureMonthApplication === "ALLOWED_CURRENT_MONTH" ? currentMonth : "";

        this.fieldConfiguration.dateConfig = {
            exclude: datesToExclude,
            minDate: startDate,
            maxDate
        };
    }

    getCreditApplicationTypes = async () => {
        const selectableTypes = await Credit.getSelectableApplicationTypes();
        this.fieldConfiguration.application_type = { options: selectableTypes };
    }

    fetchCreditFieldValues = async () => {
        const data = await CreditEntry.getFieldAllowedValues(['type']);
        this.fieldConfiguration.credit = {
            ...this.fieldConfiguration.credit,
            ...data,
        };
    }

    fetchPaymentFieldValues = (facilityPaymentTypes) => {
        const updatePaymentFieldValues = [...facilityPaymentTypes, ...['Carryover Credit', 'Discount Credit']];
        return updatePaymentFieldValues.map((paymentType) => {
            return { label: paymentType, value: paymentType }
        });
    }

    createNewCredit = async () => {
        const facilityResponse = await queryGetFacilitySettings({ facility_id: this.facilityId }, facilitySettingsQueryFields);

        const accountingMethod = facilityResponse.data.getFacilitySettings.accounting_method;

        const credit = new Credit();
        credit.fields.application_type = { value: accountingMethod };
        credit.fields.payment_type = { value: 'CHECK' };
        credit.fields.credit_entries.value = [new CreditEntry({ type: 'CREATION' })];

        this.initialState.credit = credit;
        this.initialState.applicationDate = accountingMethod === 'APPLY_CASH' ? new Date() : null;
    }

    setCreditEntryTypeLabels() {
        const types = this.fieldConfiguration.credit.type;
        const { credit } = this.initialState;
        const applicationType = credit.fields.application_type.value;

        const creditEntryTypeOptions = types.options.map(type => {
            switch (type.value) {
                case 'CREATION': {
                    if (applicationType === 'CARRYOVER_CREDIT') {
                        return { ...type, label: 'Credit Created - Carryover Credit' };
                    } else if (applicationType === 'DISCOUNT_CREDIT') {
                        return { ...type, label: 'Credit Created - Discount Credit' };
                    }
                }
                case 'OVERPAYMENT': {
                    if (applicationType === 'CARRYOVER_CREDIT') {
                        return { ...type, label: 'Credit Created - Carryover Credit' };
                    } else if (applicationType === 'DISCOUNT_CREDIT') {
                        return { ...type, label: 'Credit Created - Discount Credit' };
                    }
                }
                case 'INCREASE': {
                    let label;
                    switch (applicationType) {
                        case 'APPLY_CASH':
                            label = 'Credit Created - Cash Applied';
                            break;
                        case 'DO_NOT_APPLY_CASH':
                            label = 'Credit Created - Cash Not Applied';
                            break;
                        case 'CARRYOVER_CREDIT':
                            label = 'Credit - Carryover Credit Increase';
                            break;
                        case 'DISCOUNT_CREDIT':
                            label = 'Credit - Discount Credit Increase';
                            break;
                        default:
                            label = applicationType;
                    }

                    return { ...type, label };
                }
                case 'PAYMENT':
                    return { ...type, label: 'Credit - Used for Payment' };
                case 'REVERSAL': {
                    let appTypeLabel;
                    switch (applicationType) {
                        case 'APPLY_CASH':
                            appTypeLabel = 'Credit Cash Applied';
                            break;
                        case 'DO_NOT_APPLY_CASH':
                            appTypeLabel = 'Cash No Cash Applied';
                            break;
                        case 'CARRYOVER_CREDIT':
                            appTypeLabel = 'Carryover Credit';
                            break;
                        default:
                            appTypeLabel = applicationType;
                    }

                    return { ...type, label: `Reversal - ${appTypeLabel}` };
                }
                case 'RESTORATION':
                    let appTypeLabel;
                    switch (applicationType) {
                        case 'DISCOUNT_CREDIT':
                            appTypeLabel = 'Credit - Discount Credit Restored'
                            break;
                        default:
                            appTypeLabel = 'Credit - Restored';
                    }
                    return { ...type, label: appTypeLabel };
                case 'WRITE_OFF': {
                    let appTypeLabel;
                    switch (applicationType) {
                        case 'APPLY_CASH':
                            appTypeLabel = 'Credit Write Off - Cash Applied';
                            break;
                        case 'DO_NOT_APPLY_CASH':
                            appTypeLabel = 'Credit Write Off - Cash Not Applied';
                            break;
                        case 'CARRYOVER_CREDIT':
                            appTypeLabel = 'Credit - Carryover Credit Write Off';
                            break;
                        case 'DISCOUNT_CREDIT':
                            appTypeLabel = 'Credit - Discount Credit Write Off';
                            break;
                        default:
                            appTypeLabel = applicationType;
                    }

                    return { ...type, label: appTypeLabel };
                }
                default:
                    return type;
            }
        });

        this.fieldConfiguration.credit.type.options = creditEntryTypeOptions;
    }

    fetchCredit = async () => {
        const response = await queryGetCredit({ credit_id: this.creditId }, creditQueryFields);
        if (response.errors) {
            throw response.errors.map(error => error.message);
        }

        const credit = Credit.createFromQueryValues(response.data.getCredit);
        const subscription = credit.fields.subscription.value.fields;
        const id = `(ID: ${subscription.payor.value.fields.readable_id.value})`;

        const label = featureFlags.SUBSCRIPTION_NAME ? [subscription.subscription_name.value, id].join(' ').trim()
            : [
                subscription.payor.value.fields.billing_firstname.value,
                subscription.payor.value.fields.billing_lastname.value,
                id,
            ].join(' ').trim();

        const subscriptionId = {
            options: [{
                value: subscription.id.value,
                label: label
            }]
        };

        const accountId = `AC-${subscription.account_id.value}`;

        this.fieldConfiguration.credit = {
            ...this.fieldConfiguration.credit,
            subscription_id: subscriptionId,
            accountId,
        };

        if (featureFlags.SUBSCRIPTION_NAME) {
            this.fieldConfiguration.credit = {
                ...this.fieldConfiguration.credit,
                billingName: credit.fields.subscription.value.fields.payor.value.fields.info_name.value,
                pcName: [
                    credit.fields.subscription.value.fields.payor.value.fields.billing_firstname.value,
                    credit.fields.subscription.value.fields.payor.value.fields.billing_lastname.value,
                ].join(' ').trim(),
            }
        }

        if (credit.fields.application_type.value === 'APPLY_CASH') {
            const creditEntries = credit.fields.credit_entries.value;
            const initialCreditEntry = creditEntries.find(entry => {
                const entryType = entry.fields.type.value;
                return ['CREATION', 'OVERPAYMENT'].includes(entryType);
            });

            const applicationDate = initialCreditEntry.fields.application_date.value;
            this.initialState.applicationDate = applicationDate ? moment(applicationDate, 'YYYY-MM-DD') : null;
        }

        this.initialState.credit = credit;
        return credit;
    }

    fetchSubscriptions = async () => {
        const response = await queryListSubscriptions({ facility_id: this.facilityId }, subscriptionsQueryFields);
        if (response.errors) {
            throw response.errors.map(error => error.message);
        }

        const subscriptions = response.data.listSubscriptions.subscriptions.map(subscription =>
            Subscription.createFromQueryValues(subscription)
        );

        const subscriptionId = {
            options: [
                { value: null, label: '' },
                ...subscriptions.map(subscription => {
                    return {
                        value: subscription.fields.id.value,
                        label: [
                            subscription.fields.subscription_name.value,
                            `(AC-${subscription.fields.account_id.value})`
                        ].join(' ').trim()
                    };
                }),
            ]
        };

        this.fieldConfiguration.credit = {
            ...this.fieldConfiguration.credit,
            subscription_id: subscriptionId,
        };
    }

    async getFacilityTimezone() {
        const facility = await getFacility(this.facilityId);
        this.fieldConfiguration.facilityTimezone = facility.fields.timezone.value;
    }
}

export default CreditPanelConfig;
